import React, { useEffect, useState, useRef } from "react";
import Chat from "./common/Chat";
import { useOutletContext, useLocation } from "react-router-dom";
import { agentAPIMap } from "../assets/js/constants";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import SourceModal from "./SourceModal";
import FeedbackDropdown from "./FeedbackDropdown";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function NewChat() {
  
  const chatEndRef = useRef(null);
  const navigate = useNavigate();
  const [selectedText, setSelectedText] = useState("");
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [currentSources, setCurrentSources] = useState([]);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);  
  const [feedbackOptions, setFeedbackOptions] = useState([]);
  const [showFeedbackDropdown, setShowFeedbackDropdown] = useState(false);
  const [positiveFeedback, setPositiveFeedback] = useState([]);
  const [negativeFeedback, setNegativeFeedback] = useState([]);
  const [selectedReasonType, setSelectedReasonType] = useState(null);  
  const [tempFeedback, setTempFeedback] = useState({});
  const isFetched = useRef(false);
  const toggleDropdown = (index, reasonType) => {
    setTempFeedback(prev => ({ ...prev, [index]: reasonType }));
    setOpenDropdownIndex(index); // always open on click
    setSelectedReasonType(reasonType);
    setFeedbackOptions(reasonType === 1 ? positiveFeedback : negativeFeedback);
    setShowFeedbackDropdown(true);
  };
  const {
    selectedAgent,
    chatHistory,
    setChatHistory,
    setApiHistory,
    suggestions,
    setSuggestions,
    reloadThreads
  } = useOutletContext();

  const location = useLocation();  
  const { user } = useUser();
  const baseapiUrl = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    if (user?.user_id && !isFetched.current) {
      isFetched.current = true;
      fetchAllFeedbackReasons();      
    }
  }, [user?.user_id]);  

  const fetchAllFeedbackReasons = () => {
    const url = `${baseapiUrl.replace(/\/+$/, "")}/get_feedback_reasons/`;

    const callApi = (type) =>
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userid: user?.user_id,          
          reason_type: type,
        }),
      }).then((res) => res.json());

    // Fetch
    Promise.all([callApi(1), callApi(-1)])
      .then(([upRes, downRes]) => {
        setPositiveFeedback(upRes?.data?.feedback_reasons || []);
        setNegativeFeedback(downRes?.data?.feedback_reasons || []);
      })
      .catch((err) => console.error("Feedback API Error:", err));
  };

  const handleFeedback = async (chat, reasonType, reasonText) => {
    const url = `${baseapiUrl.replace(/\/+$/, "")}/add_feedback_to_message/`;           
    const payload = {
      userid: user?.user_id,            
      message_id: chat.id,
      reason_type: reasonType,
      reason_text: reasonText
    };

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result?.success) {
        toast.success(result.message || "Feedback submitted successfully!");   
        // UPDATE UI ICON STATE BASED ON RESPONSE
        setChatHistory((prev) =>
          prev.map((msg) =>
            msg.id == result.message_id
              ? { ...msg,                   
                  reason_type: Number(result.reason_type),
                  feedback: Number(result.reason_type),
                  reason_text: result.reason_text || "" }
              : msg
          )
        );
        setTempFeedback(prev => {
          const copy = { ...prev };
          delete copy[chatHistory.indexOf(chat)];
          return copy;
        });

      } else {
        toast.error(result?.message || "Failed to submit feedback.");
      }
    } catch (error) {
      console.error("Feedback API Error:", error);
    }
  };

  useEffect(() => {
    setChatHistory([]);
    setSuggestions([]);
    setApiHistory([]);    
  }, [selectedAgent]);

  useEffect(() => {
    if (location.state?.restoredChat) {
      const formattedChat = location.state.restoredChat.map((msg) => ({
        ...msg,
        id: msg.message_id || msg.id || null,
        answer: msg.answer ? formatBotResponse(msg.answer) : msg.answer,        
        reason_type: msg.reason_type ? Number(msg.reason_type) : null,
      }));

      setChatHistory(formattedChat);
      // Restore suggestions from last message
      const last = formattedChat[formattedChat.length - 1];
      setSuggestions(last?.suggestions || []);

      navigate(location.pathname, { replace: true, state: {} }); // clear it once used
    }
  }, [location.state?.restoredChat]);

  // reload function
  useEffect(() => {
    window.chatReloadThreads = reloadThreads;
    return () => {
      window.chatReloadThreads = null;
    };
  }, [reloadThreads]);

  function formatBotResponse(text) {
    // Escape HTML to avoid XSS
    text = text.replace(/</g, "&lt;").replace(/>/g, "&gt;");

    // Convert *bold* to <strong>bold</strong>
    text = text.replace(/\*(.*?)\*/g, "<strong>$1</strong>");

    // Handle numbered list (e.g., 1. Item)
    text = text.replace(/(^|\n)(\d+\.\s.*?)(?=\n|$)/g, (match, p1, p2) => {
      return `${p1}<li>${p2.substring(p2.indexOf(' ') + 1)}</li>`;
    });

    // Handle bullet list (e.g., - Item)
    text = text.replace(/(^|\n)(-\s.*?)(?=\n|$)/g, (match, p1, p2) => {
      return `${p1}<li>${p2.substring(2)}</li>`;
    });

    // Wrap <li> items in <ol> or <ul>
    text = text.replace(/((<li>.*?<\/li>\s*)+)/gs, (match) => {
      const isNumbered = match.match(/<li>\d+\./);
      return isNumbered ? `<ol>${match}</ol>` : `<ul>${match}</ul>`;
    });

    // Convert double newlines to <p>
    text = text
      .split(/\n{2,}/)
      .map((para) => `<p>${para.trim()}</p>`)
      .join('');

    // Optional: Convert single newlines to <br>
    // text = text.replace(/\n/g, "<br>");

    return text;
  }

  const handleResponse = (answer, question, extra = {}) => {
    if (answer === null) {    
      setChatHistory((prev) => [...prev, { question, answer: null, sources: extra.sources || [], id: extra.id || null }]);
      setSuggestions([]);
      setApiHistory([]);
      return;
    }
    
    const formattedAnswer = formatBotResponse(answer);

    setChatHistory((prev) => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      if (updated[lastIndex]?.question === question) {
        updated[lastIndex] = { ...updated[lastIndex], answer: formattedAnswer, sources: extra.sources || [], id: extra.id || updated[lastIndex]?.id || null };
      } else {
        updated.push({ question, answer: formattedAnswer, sources: extra.sources || [], id: extra.id || null });
      }
      return updated;
    });

    // Only set suggestions if the answer is NOT the "sorry" message
    if (
      extra.suggestions &&
      answer.trim() !==
      "I'm sorry, I don't have enough information to answer that. Could you provide more details?"
    ) {
      setSuggestions(extra.suggestions);
    } else {
      setSuggestions([]);
    }

    // Clear query from location.state once answer is set
    if (location.state?.query) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  };

  const handleLoading = (val) => {
    // No need to manage global loading state anymore
  };  

  useEffect(() => {

    const incomingQuery = location.state?.query;
    if (!incomingQuery) return;
    // Add question immediately (show typing dots)
    handleResponse(null, incomingQuery);
    const url = `${baseapiUrl.replace(/\/+$/, "")}/ask/`;
    const postdata = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question: incomingQuery,
        userid: user?.user_id,                   
        thread_id: user?.threadId || 0,
        timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || ""
      }),
    };

    fetch(url, postdata)
      .then((res) => res.json())
      .then((result) => {
        const answer = result?.data?.answer || "No response available.";
        const suggestions = result?.data?.suggestions || [];
        const sources = result?.data?.sources || [];
        const msgId = result?.data?.message_id || '';

        handleResponse(answer, incomingQuery, {
          suggestions,
          sources,
          id: msgId
        });

      })
      .catch((err) => {
        console.error("API Error:", err);
        handleResponse("Something went wrong. Please try again.", incomingQuery);
      });
      
  }, [location.state?.query]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]); // every time chatHistory updates

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the dropdown is open and clicked outside, close it
      if (openDropdownIndex !== null && !event.target.closest(".feedback-icons")) {
        setOpenDropdownIndex(null);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openDropdownIndex]);

  return (
    <div>
      <div className="chatdiv">
        <h3 className="text-center title">
          {/* Welcome to AI-Agent who is expert in the <b>{selectedAgent}</b> department. */}
          I am EVE, your expert infrastructure - AI. I am Here to assist and Guide you.
        </h3>

        <div className="boxes">
          {chatHistory.map((chat, index) => (
            <div key={index}>
              <div className="quesdiv">
                <div className="chat-response-question">{chat.question}</div>
              </div>
              {chat.answer === null ? (
                <div id="typingMessage">
                  <div className="typingDots">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              ) : (
                <div
                  className="chat-response"
                  dangerouslySetInnerHTML={{ __html: chat.answer }}
                ></div>
              )}

              <div className="answer-footer">
  
                {/* LEFT SIDE: SOURCES */}
                {chat.sources && chat.sources.length > 0 && (
                  <div className="sources">
                    <h5
                      className="sources_label"                      
                      onClick={() => {
                        setCurrentSources(chat.sources);
                        setShowSourcesModal(true);
                      }}
                    >                      
                      <i className="fa-solid fa-link"></i>
                      Sources
                    </h5>
                  </div>
                )}

                {/* RIGHT SIDE: FEEDBACK */}
                {chat.answer !== null && (
                  <div className="feedback-icons">
                    <div className="icon-wrapper">                    
                      <i
                        className={`fa-regular fa-thumbs-up thumbsup ${
                          chat.reason_type == 1 ? "feedback-selected" : ""
                        }`}
                        onClick={() => toggleDropdown(index, 1)}
                        title="Good Response"
                      />                    
                      <i
                        className={`fa-regular fa-thumbs-down thumbsdown ${
                          chat.reason_type == -1 ? "feedback-selected" : ""
                        }`}
                        onClick={() => toggleDropdown(index, -1)}
                        title="Bad Response"
                      />
                    </div>

                    {/* Dynamic feedback dropdown */}
                    {openDropdownIndex === index && showFeedbackDropdown && (
                      <FeedbackDropdown
                        options={feedbackOptions}
                        onSelect={(reasonText) => {
                          handleFeedback(chatHistory[index], selectedReasonType, reasonText);
                          setOpenDropdownIndex(null);
                        }}
                      />
                    )}
                  </div>
                )}
              </div>

              {showSourcesModal && (
                <SourceModal
                  show={showSourcesModal}
                  sources={currentSources}
                  onClose={() => setShowSourcesModal(false)}
                />
              )}

            </div>
          ))}

          <div ref={chatEndRef} />

          {suggestions.length > 0 && (
            <div className="suggestions-container">
              <h5>Suggestions:</h5>
              <ul className="suggestion-list">
                {suggestions.map((text, idx) => (
                  <li
                    key={idx}
                    className="suggestion-item"
                    onClick={() => {
                      setSelectedText("");
                      setTimeout(() => setSelectedText(text), 0);
                    }}
              
                  >
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <Chat
        selectedText={selectedText}
        onResponse={handleResponse}
        onLoading={handleLoading}
        apiBaseUrl={baseapiUrl}
        onAskSuccess={() => window.chatReloadThreads?.()}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

    </div>
  );
}